import { signal, computed, batch } from '@preact/signals-core';
import { contentParameters, isRitePath, RITE_TITLES, nextHour, isCursus, suggestOccasion } from './routing.js';
import { makePath } from '../../shared/js/rite-paths.js';

async function fetchRite(path) {
  let contentParams = contentParameters(path);
  return fetch(`/api/rite?loc=${contentParams.locale}&date=${contentParams.date}&s=${contentParams.select}&occasion=${contentParams.occasion}+${contentParams.prayerType}&v=${contentParams.votives.join('+')}`).then(resp => resp.text());
}

// Day title straight from the rendered rite's <h1 class="large-title">, without the trailing period
// rite_title() (renderer/rendering_utils.py) always appends
function dayTitle(rite) {
  let match = rite.match(/<h1 class="large-title">(.*?)<\/h1>/);
  return match ? match[1].replace(/\.$/, '') : '';
}

export function makePrayStore(liturgyStore) {
  // Always a rite path once init() has run, since a bare /pray visit is immediately redirected; bindings rely on this
  const displayPath = signal(window.location.pathname + window.location.search);
  // Document is hydrated (after a manner of speaking) so initial value of rite should be the already-provided HTML. Rites
  // far from today are left for this script to load (datamanage.is_server_rendered()).
  const main = document.querySelector('main');
  const serverRendered = main.dataset.serverRendered == 'true';
  const rite = signal(main.innerHTML);
  const opt = signal((() => {
    let optMatch = document.cookie.match(/(?:^|;\s*)opt=([^;]*)/);
    return optMatch ? decodeURIComponent(optMatch[1]).split('+').filter(t => t) : [];
  })());
  // Null suggests that a hardlink was manually pasted into the browser. This is handled within the user flow and is 'corrected' over to either hard or soft.
  const navigationType = signal(history.state?.navigationType ?? null);

  const lastCursusHour = signal((() => {
    try {
      let lastCompleted = JSON.parse(localStorage.getItem('lastCursusHour') || 'null');
      return lastCompleted ? {...lastCompleted, date: Temporal.PlainDate.from(lastCompleted.date)} : null;
    } catch {
      return null;
    }
  })());
  const now = signal(Temporal.Now.plainDateTimeISO());

  const contentParams = computed(() => contentParameters(displayPath.value));
  // The rite whose text is on the page. displayPath moves as soon as a navigation starts, so links follow at once;
  // this moves with the text itself, for what belongs to the rite shown (the next-hour button, marking it prayed).
  const ritePath = signal(displayPath.value);
  const riteParams = computed(() => contentParameters(ritePath.value));
  // The navigation type of the rite on the page: moves with ritePath when a navigation finishes loading, or at once
  // when the type is changed without navigating
  const riteNavigationType = signal(navigationType.value);

  // /api/rite responses for one context, by date and occasion. The context is everything else that changes a
  // response: locale, select, votives, and the opt cookie (read by the server, not sent in the query). Any change of
  // context discards them all. Holds the pending promise, so a hover's prefetch and the click share one request.
  let riteCache = new Map();
  let riteCacheContext = null;

  function riteContext(params) {
    return [params.locale, params.select, params.votives.join('+'), opt.value.join('+')].join('|');
  }

  function riteKey(params) {
    return `${params.date}|${params.occasion}+${params.prayerType}`;
  }

  function cachedRite(path) {
    let params = contentParameters(path);
    let context = riteContext(params);
    if (context != riteCacheContext) {
      riteCache = new Map();
      riteCacheContext = context;
    }
    let cache = riteCache;
    let key = riteKey(params);
    if (!cache.has(key)) {
      // A failed request isn't kept, so the rite can be fetched again
      cache.set(key, fetchRite(path).catch(error => {
        cache.delete(key);
        throw error;
      }));
    }
    return cache.get(key);
  }

  // The rite rendered into the page by the server is the first entry
  if (serverRendered && isRitePath(displayPath.value)) {
    let params = contentParameters(displayPath.value);
    riteCacheContext = riteContext(params);
    riteCache.set(riteKey(params), Promise.resolve(rite.value));
  }

  async function loadRite(path) {
    let html = await cachedRite(path);
    // A later navigation has superseded this one
    if (displayPath.value != path) return;
    batch(() => {
      rite.value = html;
      ritePath.value = path;
      riteNavigationType.value = navigationType.value;
    });
    document.title = `${RITE_TITLES[contentParameters(path).occasion]} | ${dayTitle(html)}`;
    // Same form as the server's canonical path, on the site's address that the server rendered
    let canonical = document.querySelector('link[rel="canonical"]');
    canonical.href = new URL(makePath(contentParameters(path)), canonical.href).href;
    window.scrollTo(0, 0);
  }

  async function navigateRite(path, newNavigationType=null, action='push') {
    // NavigationType is only changed if explicitly specified by some action since hard vs soft navigationType significantly changes user flow; but many actions are the same between and therefore don't specify a type.
    batch(() => {
      if (newNavigationType) {
        navigationType.value = newNavigationType;
      }
      displayPath.value = path;
    });
    if (action == 'push') {
      history.pushState({navigationType: navigationType.value}, '', path);
    } else {
      history.replaceState({navigationType: navigationType.value}, '', path);
    }
    await loadRite(path);
  }

  async function setOpt(tags) {
    opt.value = tags;
    let cookie = tags.filter(t => t).join('+');
    if (cookie) {
      // cookieStore defaults to strict, which withholds the cookie from page loads reached from another site
      await cookieStore.set({name: 'opt', value: cookie, path: '/', sameSite: 'lax'});
    } else {
      await cookieStore.delete({name: 'opt', path: '/'});
    }
  }

  function bestHour() {
    let locale = window.location.pathname.match(/^\/([a-z]{2})\//)?.[1] || 'en';
    let currentNow = now.value;
    if (lastCursusHour.value === null || !canIncrementHour(lastCursusHour.value, currentNow)) {
      return {
        locale: locale,
        prayerType: 'officium',
        date: currentNow.toPlainDate(),
        select: 'primarium',
        occasion: suggestOccasion(currentNow),
        votives: liturgyStore.votives.value
      }
    } else {
      return {...nextHour(lastCursusHour.value), locale: locale, prayerType: 'officium', votives: liturgyStore.votives.value };
    }
  }

  return {
    displayPath: displayPath,
    rite: rite,
    opt: opt,
    navigationType: navigationType,
    now: now,
    updateNow: () => {
      now.value = Temporal.Now.plainDateTimeISO();
    },
    contentParams: contentParams,
    // Null until a rite is on the page (a bare /pray visit before its first rite loads)
    nextHourButton: computed(() => isRitePath(ritePath.value) ? nextHourTarget(riteParams.value, lastCursusHour.value, now.value, riteNavigationType.value) : null),
    navigateRite: navigateRite,
    // Starts fetching a rite the reader is about to choose. A rite of another context is left alone, so merely
    // pointing at it doesn't discard the current context's rites.
    prefetchRite: (path) => {
      if (isRitePath(path) && riteContext(contentParameters(path)) == riteCacheContext) {
        cachedRite(path);
      }
    },
    bestHour: bestHour,
    // Leaves a pinned rite for the most relevant one; a new history entry, so Back returns to where the user was
    returnToCurrent: async () => {
      await navigateRite(makePath(bestHour()), 'soft');
    },
    // Changes the navigation type without navigating, recording it in the current history entry so it survives a reload
    setNavigationType: (newNavigationType) => {
      batch(() => {
        navigationType.value = newNavigationType;
        riteNavigationType.value = newNavigationType;
      });
      history.replaceState({...history.state, navigationType: newNavigationType}, '');
    },
    // Redirects a bare /pray visit to the most relevant rite, and loads a rite the server left out
    init: () => {
      if (!isRitePath(window.location.pathname)) {
        navigateRite(makePath(bestHour()), 'soft', 'replace');
      } else if (!serverRendered) {
        loadRite(displayPath.value);
      }
    },
    // Back/forward only re-displays the entry; it must not push a new one
    handlePopstate: async () => {
      if (!isRitePath(window.location.pathname)) {
        await navigateRite(makePath(bestHour()), 'soft', 'replace');
        return;
      }
      let path = window.location.pathname + window.location.search;
      batch(() => {
        navigationType.value = history.state?.navigationType ?? null;
        displayPath.value = path;
      });
      await loadRite(path);
    },
    markHourAsDone: () => {
      let current = riteParams.value;
      if (!isCursus(current)) {
        return;
      }
      let hour = {date: current.date, prayerType: current.prayerType, select: current.select, occasion: current.occasion, votives: current.votives};
      lastCursusHour.value = hour;
      try {
        localStorage.setItem('lastCursusHour', JSON.stringify({...hour, date: hour.date.toString()}));
      } catch {
        // Some browsers throw an Error if localStorage is disabled (who does this?????)
      }
    },
    toggleVotive: async (tag) => {
      let current = contentParams.value;
      liturgyStore.toggleVotive(tag);
      await navigateRite(makePath({...current, votives: liturgyStore.votives.value}));
    },
    setDesired: async (select, optTag) => {
      let current = contentParams.value;
      let tags = opt.value.filter(t => t == 'privata');
      if (optTag) tags.push(optTag);
      await setOpt(tags);
      if (current.select != select) {
        await navigateRite(makePath({...current, select: select}));
      } else {
        await loadRite(displayPath.value);
      }
    },
    togglePriest: async () => {
      let tags = opt.value.includes('privata') ? opt.value.filter(t => t != 'privata') : [...opt.value, 'privata'];
      await setOpt(tags);
      await loadRite(displayPath.value);
    }
  }
}

// The next moment canSay's answer can change: midnight, when the date moves on, and 14:00, from which the next day's
// Matins and Lauds may be anticipated
export function nextCanSayChange(now) {
  let today = now.toPlainDate();
  return now.hour < 14 ? today.toPlainDateTime({hour: 14}) : today.add({days: 1}).toPlainDateTime();
}

export function canSay(params, now) {
  if (params.occasion == 'matutinum-laudes' && Temporal.PlainDate.compare(params.date, now.toPlainDate().add({days: 1})) == 0) {
    return now.hour >= 14;
  }
  return Temporal.PlainDate.compare(params.date, now.toPlainDate()) == 0;
}

// The day other rite links lead to. Soft navigation onto Matins and Lauds anticipated the evening before still
// belongs to the current calendar day, so links lead to that day's rites rather than the anticipated day's.
export function riteLinksDate(params, navigationType, now) {
  let today = now.toPlainDate();
  let anticipatedMatins = isCursus(params) && params.occasion == 'matutinum-laudes' && Temporal.PlainDate.compare(params.date, today.add({days: 1})) == 0;
  return navigationType == 'soft' && anticipatedMatins ? today : params.date;
}

export function canIncrementHour(current, now) {
  return canSay(nextHour(current), now);
}

// What the next-hour button offers: the hour after the current one when on a cursus hour, otherwise the hour
// after the last one prayed (today's Matins and Lauds counts as prayed when none has been recorded yet). Hard navigation
// pins the reader to the rite they chose, so the next hour is never offered there.
export function nextHourTarget(current, lastCursusHour, now, navigationType) {
  let reference = isCursus(current) ? current : lastCursusHour ?? {...current, prayerType: 'officium', date: now.toPlainDate(), select: 'primarium', occasion: 'matutinum-laudes', votives: []};
  let target = nextHour(reference);
  return {
    // Stored hours carry no locale, and ones recorded before prayerType was stored lack it; the cursus is always the Office
    path: makePath({...target, locale: current.locale, prayerType: 'officium'}),
    occasion: target.occasion,
    allowed: navigationType != 'hard' && canSay(target, now)
  };
}
